import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { playNotificationSound } from '../notifications';

export enum NotificationType {
  ContentGenerationSuccess = 'content_generation_success',
  ContentGenerationError = 'content_generation_error',
  ProductGenerationSuccess = 'product_generation_success',
  ProductGenerationError = 'product_generation_error',
}

export enum NotificationPriority {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export type Notification = {
  id: string;
  type: NotificationType;
  createdBy: string; // User ID who triggered the action
  contentId?: string; // Reference to related content
  contentTitle?: string; // Display title
  message?: string; // Custom message for flexible notifications
  createdAt: number; // Timestamp
  isRead: boolean; // Has been viewed
  isPlayed: boolean; // Sound was played (for owner)
  priority: NotificationPriority; // For filtering/sorting
  metadata?: Record<string, unknown>; // Extensible data for specific notification types
};

type NotificationInput = Omit<Notification, 'id' | 'createdAt' | 'isRead' | 'isPlayed'>;

type StoreState = {
  notifications: Notification[];
  unreadCount: number;
};

type StoreActions = {
  addNotification: (notification: NotificationInput) => Notification | null;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  markAsPlayed: (id: string) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  getUnreadNotifications: () => Notification[];
  getAllNotifications: () => Notification[];
  getNotificationsByType: (type: NotificationType) => Notification[];
};

type FullStoreState = StoreState & StoreActions;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

export const useStore = create<FullStoreState>()(
  devtools(
    function (set, get) {
      const store: FullStoreState = {
        notifications: [],
        unreadCount: 0,

        addNotification: function (input: NotificationInput): Notification | null {
          const notifications = get().getNotificationsByType(input.type);
          if (notifications.length > 0 && notifications[0].contentId === input.contentId) {
            return null;
          }
          playNotificationSound();
          const notification: Notification = {
            ...input,
            id: generateId(),
            createdAt: Date.now(),
            isRead: false,
            isPlayed: true,
          };

          set(function (state) {
            return {
              notifications: [notification, ...state.notifications],
              unreadCount: state.unreadCount + 1,
            };
          });

          return notification;
        },

        markAsRead: function (id: string) {
          set(function (state) {
            const notification = state.notifications.find(function (n) {
              return n.id === id;
            });

            if (notification == null || notification.isRead) {
              return state;
            }

            return {
              notifications: state.notifications.map(function (n) {
                if (n.id === id) {
                  return { ...n, isRead: true };
                }
                return n;
              }),
              unreadCount: Math.max(0, state.unreadCount - 1),
            };
          });
        },

        markAllAsRead: function () {
          set(function (state) {
            return {
              notifications: state.notifications.map(function (n) {
                return { ...n, isRead: true };
              }),
              unreadCount: 0,
            };
          });
        },

        markAsPlayed: function (id: string) {
          set(function (state) {
            return {
              notifications: state.notifications.map(function (n) {
                if (n.id === id) {
                  return { ...n, isPlayed: true };
                }
                return n;
              }),
            };
          });
        },

        removeNotification: function (id: string) {
          set(function (state) {
            const notification = state.notifications.find(function (n) {
              return n.id === id;
            });

            const wasUnread = notification != null && !notification.isRead;

            return {
              notifications: state.notifications.filter(function (n) {
                return n.id !== id;
              }),
              unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
            };
          });
        },

        clearAll: function () {
          set(function () {
            return {
              notifications: [],
              unreadCount: 0,
            };
          });
        },

        getAllNotifications: function () {
          return get().notifications;
        },

        getUnreadNotifications: function () {
          return get().notifications.filter(function (n) {
            return !n.isRead;
          });
        },

        getNotificationsByType: function (type: NotificationType) {
          return get().notifications.filter(function (n) {
            return n.type === type;
          });
        },
      };

      return store;
    },
    { name: 'blym', store: 'notifications' }
  )
);
